import { supabase } from '@/lib/supabase';
import { z } from 'zod';

export interface Pet {
  id: string;
  name: string;
  breed: string;
  age_years: number | null;
  age_months: number | null;
  gender: string;
  size: string | null;
  color: string | null;
  category: string | null;
  pet_type: string;
  status: string | null;
  image_url: string | null;
  additional_images: string[] | null;
  description: string | null;
  personality_traits: string[] | null;
  energy_level: string | null;
  good_with_kids: boolean | null;
  good_with_pets: boolean | null;
  health_status: string | null;
  spayed_neutered: boolean | null;
  vaccination_status: string | null;
  adoption_fee: number | null;
  shelter_location: string | null;
  budget_min: number | null;
  budget_max: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PetFilters {
  category?: string;
  status?: string;
  limit?: number;
  page?: number;
  breed?: string;
  budgetMin?: number;
  budgetMax?: number;
  ageMin?: number;
  ageMax?: number;
  location?: string;
  gender?: string;
  size?: string;
  goodWithKids?: boolean;
  goodWithPets?: boolean;
  energyLevel?: string;
}

const uuidSchema = z.string().uuid();

/**
 * Validate if a string is a valid UUID
 */
function isValidUUID(id: string): boolean {
  return uuidSchema.safeParse(id).success;
}

/**
 * Fetch all pets from the database with optional filters
 */
export async function fetchPets(filters?: PetFilters, signal?: AbortSignal): Promise<{ data: Pet[] | null; count: number | null; error: any }> {
  try {
    let query = supabase
      .from('pets')
      .select('*', { count: 'exact' });

    if (signal) {
      query = query.abortSignal(signal);
    }

    query = query
      .eq('status', filters?.status || 'available')
      .order('created_at', { ascending: false });

    // Category filter (species)
    if (filters?.category) {
      query = query.eq('category', filters.category);
    }

    // Breed filter
    if (filters?.breed) {
      query = query.ilike('breed', `%${filters.breed}%`);
    }

    // Budget filters
    if (filters?.budgetMin !== undefined) {
      query = query.gte('adoption_fee', filters.budgetMin);
    }
    if (filters?.budgetMax !== undefined) {
      query = query.lte('adoption_fee', filters.budgetMax);
    }

    // Age filters (in years)
    if (filters?.ageMin !== undefined) {
      query = query.gte('age_years', filters.ageMin);
    }
    if (filters?.ageMax !== undefined) {
      query = query.lte('age_years', filters.ageMax);
    }

    // Location filter
    if (filters?.location) {
      query = query.ilike('shelter_location', `%${filters.location}%`);
    }

    // Gender filter
    if (filters?.gender) {
      query = query.eq('gender', filters.gender);
    }

    // Size filter
    if (filters?.size) {
      query = query.eq('size', filters.size);
    }

    // Good with kids filter
    if (filters?.goodWithKids !== undefined) {
      query = query.eq('good_with_kids', filters.goodWithKids);
    }

    // Good with pets filter
    if (filters?.goodWithPets !== undefined) {
      query = query.eq('good_with_pets', filters.goodWithPets);
    }

    // Energy level filter
    if (filters?.energyLevel) {
      query = query.eq('energy_level', filters.energyLevel);
    }

    // Pagination
    if (filters?.page && filters?.limit) {
      const from = (filters.page - 1) * filters.limit;
      const to = from + filters.limit - 1;
      query = query.range(from, to);
    } else if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching pets:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      return { data: null, count: null, error };
    }

    return { data: data as Pet[], count, error: null };
  } catch (error: any) {
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      // Ignore abort errors
      return { data: null, count: null, error: error };
    }
    console.error('Unexpected error fetching pets:', error);
    return { data: null, count: null, error };
  }
}

/**
 * Fetch a single pet by ID
 */
export async function fetchPetById(id: string, signal?: AbortSignal): Promise<{ data: Pet | null; error: any }> {
  try {
    const validId = uuidSchema.safeParse(id);
    if (!validId.success) {
      const error = {
        code: 'INVALID_UUID',
        message: `Invalid pet ID format: "${id}".`,
        details: validId.error.format(),
      };
      console.error('Invalid UUID format:', error);
      return { data: null, error };
    }

    let query = supabase
      .from('pets')
      .select('*')
      .eq('id', id);

    if (signal) {
      query = query.abortSignal(signal);
    }

    const { data, error } = await query.single();

    if (error) {
      console.error('Error fetching pet:', error);
      return { data: null, error };
    }

    return { data: data as Pet, error: null };
  } catch (error: any) {
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      return { data: null, error };
    }
    console.error('Unexpected error fetching pet:', error);
    return { data: null, error };
  }
}

/**
 * Save a pet for a user
 */
export async function savePet(userId: string, petId: string): Promise<{ success: boolean; error: any }> {
  try {
    if (!isValidUUID(userId) || !isValidUUID(petId)) {
      return { success: false, error: 'Invalid ID format' };
    }

    const { error } = await supabase
      .from('saved_pets')
      .insert({ user_id: userId, pet_id: petId });

    if (error) {
      // Ignore unique constraint violations (already saved)
      if (error.code === '23505') {
        return { success: true, error: null };
      }
      console.error('Error saving pet:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Unexpected error saving pet:', error);
    return { success: false, error };
  }
}

/**
 * Unsave a pet for a user
 */
export async function unsavePet(userId: string, petId: string): Promise<{ success: boolean; error: any }> {
  try {
    if (!isValidUUID(userId) || !isValidUUID(petId)) {
      return { success: false, error: 'Invalid ID format' };
    }

    const { error } = await supabase
      .from('saved_pets')
      .delete()
      .match({ user_id: userId, pet_id: petId });

    if (error) {
      console.error('Error unsaving pet:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Unexpected error unsaving pet:', error);
    return { success: false, error };
  }
}

/**
 * Check if a pet is saved by the user
 */
export async function isPetSaved(userId: string, petId: string): Promise<boolean> {
  try {
    if (!isValidUUID(userId) || !isValidUUID(petId)) {
      return false;
    }

    const { data, error } = await supabase
      .from('saved_pets')
      .select('id')
      .match({ user_id: userId, pet_id: petId })
      .maybeSingle();

    if (error) return false;
    return !!data;
  } catch {
    return false;
  }
}

/**
 * Format age display string from years and months
 */
export function formatAge(years: number | null, months: number | null): string {
  if (years === null && months === null) return 'Age unknown';
  if (years && months) return `${years} year${years > 1 ? 's' : ''}, ${months} month${months > 1 ? 's' : ''}`;
  if (years) return `${years} year${years > 1 ? 's' : ''}`;
  if (months) return `${months} month${months > 1 ? 's' : ''}`;
  return 'Age unknown';
}

/**
 * Get category display name
 */
export function getCategoryDisplay(category: string | null): string {
  if (!category) return 'Unknown';

  const categoryMap: { [key: string]: string } = {
    'dogs_puppies': 'Dog',
    'cats_kittens': 'Cat',
    'birds': 'Bird',
    'rabbits': 'Rabbit',
    'fish': 'Fish',
    'exotic_animals': 'Exotic'
  };

  return categoryMap[category] || category;
}

/**
 * Get unique breeds for a given category
 */
export async function fetchBreedsByCategory(category: string): Promise<{ data: string[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('pets')
      .select('breed')
      .eq('category', category)
      .order('breed');

    if (error) {
      console.error('Error fetching breeds:', error);
      return { data: null, error };
    }

    // Extract unique breeds
    const uniqueBreeds = [...new Set(data?.map(item => item.breed).filter(Boolean))] as string[];
    return { data: uniqueBreeds, error: null };
  } catch (error) {
    console.error('Unexpected error fetching breeds:', error);
    return { data: null, error };
  }
}

/**
 * Get unique locations from shelter_location
 */
export async function fetchLocations(): Promise<{ data: string[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('pets')
      .select('shelter_location')
      .order('shelter_location');

    if (error) {
      console.error('Error fetching locations:', error);
      return { data: null, error };
    }

    // Extract unique locations
    const uniqueLocations = [...new Set(data?.map(item => item.shelter_location).filter(Boolean))] as string[];
    return { data: uniqueLocations, error: null };
  } catch (error) {
    console.error('Unexpected error fetching locations:', error);
    return { data: null, error };
  }
}

/**
 * Convert category enum to display name
 */
export function mapCategoryToEnum(displayCategory: string): string | undefined {
  const categoryMap: { [key: string]: string } = {
    'Dog': 'dogs_puppies',
    'Cat': 'cats_kittens',
    'Bird': 'birds',
    'Rabbit': 'rabbits',
    'Fish': 'fish',
    'Exotic': 'exotic_animals'
  };

  return categoryMap[displayCategory];
}

/**
 * Fetch min and max adoption fees for a category
 */
export async function fetchAdoptionFeeRange(category: string): Promise<{ min: number; max: number; error: any }> {
  try {
    const { data, error } = await supabase
      .from('pets')
      .select('adoption_fee')
      .eq('category', category)
      .not('adoption_fee', 'is', null);

    if (error) {
      console.error('Error fetching adoption fees:', error);
      return { min: 0, max: 0, error };
    }

    if (!data || data.length === 0) {
      return { min: 0, max: 0, error: null };
    }

    const fees = data.map(pet => pet.adoption_fee as number);
    const min = Math.min(...fees);
    const max = Math.max(...fees);

    return { min, max, error: null };
  } catch (error) {
    console.error('Unexpected error fetching adoption fees:', error);
    return { min: 0, max: 0, error };
  }
}

export const petService = {
  async getById(id: string, signal?: AbortSignal): Promise<any | null> {
    const { data } = await fetchPetById(id, signal);
    if (!data) return null;
    return {
      ...data,
      ageYears: data.age_years,
      ageMonths: data.age_months,
      imageUrl: data.image_url,
      shelterLocation: data.shelter_location,
      additionalImages: data.additional_images,
      personalityTraits: data.personality_traits,
      energyLevel: data.energy_level,
      goodWithKids: data.good_with_kids,
      goodWithPets: data.good_with_pets,
      healthStatus: data.health_status,
      spayedNeutered: data.spayed_neutered,
      vaccinationStatus: data.vaccination_status,
      adoptionFee: data.adoption_fee,
      budgetMin: data.budget_min,
      budgetMax: data.budget_max,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },
  async getPets(filters?: PetFilters, signal?: AbortSignal): Promise<{ data: any[] | null; count: number | null }> {
    const { data, count } = await fetchPets(filters, signal);
    if (!data) return { data: null, count: null };
    return {
      data: data.map(pet => ({
        ...pet,
        ageYears: pet.age_years,
        ageMonths: pet.age_months,
        imageUrl: pet.image_url,
        shelterLocation: pet.shelter_location,
        additionalImages: pet.additional_images,
        personalityTraits: pet.personality_traits,
        energyLevel: pet.energy_level,
        goodWithKids: pet.good_with_kids,
        goodWithPets: pet.good_with_pets,
        healthStatus: pet.health_status,
        spayedNeutered: pet.spayed_neutered,
        vaccinationStatus: pet.vaccination_status,
        adoptionFee: pet.adoption_fee,
        budgetMin: pet.budget_min,
        budgetMax: pet.budget_max,
        createdAt: pet.created_at,
        updatedAt: pet.updated_at,
      })),
      count
    };
  },
  async getBreedsByCategory(category: string): Promise<string[] | null> {
    const { data } = await fetchBreedsByCategory(category);
    return data;
  },
  async getLocations(): Promise<string[] | null> {
    const { data } = await fetchLocations();
    return data;
  },
  async savePet(userId: string, petId: string) {
    return savePet(userId, petId);
  },
  async unsavePet(userId: string, petId: string) {
    return unsavePet(userId, petId);
  },
  async isSaved(userId: string, petId: string) {
    return isPetSaved(userId, petId);
  }
};
